// Small dot-style badge showing drawing sign-off state (who approved / requested a change, and when).
export const DrawingApprovalBadge = ({ approval, testid = "drawing-approval-badge" }) => {
  const ap = approval || {};
  const known = ["approved", "change_requested", "requested"];
  if (!known.includes(ap.status)) return null;

  const approved = ap.status === "approved";
  const awaiting = ap.status === "requested";
  let when = "";
  if (ap.at) {
    const d = new Date(ap.at);
    if (!Number.isNaN(d.getTime())) {
      when = d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
  }

  const label = approved
    ? "Drawing approved"
    : awaiting
      ? "Awaiting sign-off"
      : "Change requested";
  const title = approved
    ? "Drawing approved"
    : awaiting
      ? "Drawing sent — waiting on the customer to sign off"
      : "Drawing change requested";

  return (
    <span
      data-testid={testid}
      title={`${title}${ap.name ? ` by ${ap.name}` : ""}${when ? ` on ${when}` : ""}`}
      className={`inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider ${
        approved ? "text-emerald-300" : "text-amber-300"
      }`}
    >
      <span
        className={`h-2.5 w-2.5 shrink-0 rounded-full ${
          approved ? "bg-emerald-400" : "bg-amber-400"
        }`}
      />
      {label}
      {!awaiting && (ap.name || when) && (
        <span className="font-normal normal-case tracking-normal text-zinc-400">
          {ap.name || ""}
          {ap.name && when ? " · " : ""}
          {when}
        </span>
      )}
    </span>
  );
};

const TIMELINE_META = {
  requested: { dot: "bg-blue-400", label: "Approval requested" },
  change_requested: { dot: "bg-amber-400", label: "Change requested" },
  approved: { dot: "bg-emerald-400", label: "Approved" },
};

const fmtStamp = (at) => {
  if (!at) return "";
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

// Collapsible sign-off timeline: every request / change / approval with its note.
export const DrawingApprovalTimeline = ({
  log,
  testid = "drawing-approval-timeline",
}) => {
  const items = (log || []).filter((e) => e && e.action);
  if (!items.length) return null;
  return (
    <details
      data-testid={testid}
      className="mt-2 rounded-lg border border-zinc-800 bg-zinc-900/60 px-3 py-2"
    >
      <summary
        data-testid={`${testid}-toggle`}
        className="cursor-pointer text-[11px] font-semibold uppercase tracking-widest text-zinc-400 hover:text-zinc-200"
      >
        Drawing sign-off history · {items.length}
      </summary>
      <ol className="mt-2 space-y-2">
        {items.map((e, i) => {
          const meta = TIMELINE_META[e.action] || {
            dot: "bg-zinc-400",
            label: e.action,
          };
          const when = fmtStamp(e.at);
          return (
            <li
              key={i}
              data-testid={`${testid}-item-${i}`}
              className="flex gap-2 text-sm"
            >
              <span
                className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${meta.dot}`}
              />
              <div className="min-w-0">
                <div className="text-zinc-200">
                  <span className="font-semibold">{meta.label}</span>
                  {e.name ? <span className="text-zinc-400"> · {e.name}</span> : null}
                  {when ? <span className="text-zinc-500"> · {when}</span> : null}
                </div>
                {e.note ? (
                  <div className="mt-0.5 text-zinc-400">&ldquo;{e.note}&rdquo;</div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>
    </details>
  );
};
